// ============================================================================
// LandingPricingTabs — Pricing-секция лендинга с реальными тарифами из БД
// ============================================================================
// Клиентский компонент с переключателем между Forwarder/Importer тарифами.
// Тарифы приходят с сервера (из subscription_plans), фильтруются по audience.
// Free/Trial показываются отдельной заметкой сверху.
// ============================================================================

'use client'

import { Link } from '@/lib/i18n/navigation'
import { useEffect, useState } from 'react'
import { Check, ArrowRight, Truck, Gavel, Sparkles, Gift } from 'lucide-react'
import { planLimits, storageSize, type PlanLimitKey, type SubscriptionPlan } from '@/lib/billing'
import { soldModules } from '@/lib/modules/registry'
// Тексты модулей — из переводов, единственного их источника.
import { moduleTitle, moduleSummary } from '@/lib/modules/i18n'
import { MONO } from '@/lib/marketing/fonts'
import { SLATE } from '@/lib/design/tokens'

type Role = 'forwarder' | 'importer'
type Locale = 'ru' | 'ro' | 'en' | 'uk'

interface LandingPricingTabsProps {
  locale: string
  plans: SubscriptionPlan[]
  ctaText: string
  ctaHref: string
}

// Локализованные тексты секции
const I18N: Record<Locale, {
  badge: string
  title: string
  subtitle: string
  freeNote: string
  addonBadge: string
  addonCta: string
  addonLater: string
  addonYear: string
  forwarderTab: string
  importerTab: string
  monthly: string
  yearly: string
  yearlyDiscount: string
  perMonth: string
  perYear: string
  yearlyEquivalent: string
  popular: string
  yearlyHint: string
  freeWord: string
  ctaFree: string
  priceFrom: string
  pricePer: string
  units: { check: string; other: string }
  /** Лимиты тарифа: подписи строк, «без ограничений», единицы хранилища. */
  limits: Record<PlanLimitKey | 'unlimited' | 'mb' | 'gb', string>
}> = {
  ru: {
    badge: 'Тарифы',
    title: 'Прозрачные цены',
    subtitle: 'Выберите подходящий тариф для вашего типа бизнеса. Можно менять в любой момент.',
    freeNote: 'Каждый новый аккаунт получает 14 дней бесплатного триала. Дополнения подключаются отдельно — при регистрации или позже',
    addonBadge: 'Дополнение',
    addonCta: 'Выбрать при регистрации',
    addonLater: 'Не выбрали сразу — добавите позже в разделе «Тарифы»',
    addonYear: '{y} € в год — два месяца в подарок',
    forwarderTab: 'Я перевозчик или экспедитор',
    importerTab: 'Я грузовладелец',
    monthly: 'Помесячно',
    yearly: 'Годовая',
    yearlyDiscount: '−17%',
    perMonth: '/мес',
    perYear: '/год',
    yearlyEquivalent: '/мес при оплате за год',
    popular: 'Популярный',
    yearlyHint: 'Экономия 2 месяца оплаты — годовая подписка идёт со скидкой 17%',
    freeWord: 'Бесплатно',
    ctaFree: 'Начать бесплатно',
    priceFrom: 'от ',
    pricePer: '€ за',
    units: { check: 'проверку', other: 'штуку' },
    limits: { seats: 'Сотрудники', tenders: 'Тендеров в месяц', deals: 'Сделок в месяц', partners: 'Партнёров', landings: 'Лендингов', bots: 'Telegram-ботов', storage: 'Хранилище', unlimited: 'без ограничений', mb: 'МБ', gb: 'ГБ' },
  },
  ro: {
    badge: 'Tarife',
    title: 'Prețuri transparente',
    subtitle: 'Alegeți tariful potrivit pentru tipul dvs. de afacere. Puteți schimba oricând.',
    freeNote: 'Fiecare cont nou primește 14 zile gratuite. Suplimentele se activează separat — la înregistrare sau mai târziu',
    addonBadge: 'Supliment',
    addonCta: 'Alege la înregistrare',
    addonLater: 'Nu ați ales acum — îl adăugați mai târziu în secțiunea «Planuri»',
    addonYear: '{y} € pe an — două luni cadou',
    forwarderTab: 'Sunt transportator sau expeditor',
    importerTab: 'Sunt proprietar de marfă',
    monthly: 'Lunar',
    yearly: 'Anual',
    yearlyDiscount: '−17%',
    perMonth: '/lună',
    perYear: '/an',
    yearlyEquivalent: '/lună la plata anuală',
    popular: 'Popular',
    yearlyHint: 'Economisiți 2 luni de plată — abonamentul anual vine cu reducere de 17%',
    freeWord: 'Gratuit',
    ctaFree: 'Începeți gratuit',
    priceFrom: 'de la ',
    pricePer: '€ per',
    units: { check: 'verificare', other: 'bucată' },
    limits: { seats: 'Angajați', tenders: 'Licitații pe lună', deals: 'Tranzacții pe lună', partners: 'Parteneri', landings: 'Pagini de prezentare', bots: 'Boți Telegram', storage: 'Spațiu de stocare', unlimited: 'fără limită', mb: 'MB', gb: 'GB' },
  },
  en: {
    badge: 'Pricing',
    title: 'Transparent prices',
    subtitle: 'Choose the right plan for your business type. Switch anytime.',
    freeNote: 'Every new account gets a 14-day free trial. Add-ons are enabled separately — at sign-up or later',
    addonBadge: 'Add-on',
    addonCta: 'Choose at sign-up',
    addonLater: 'Did not choose now — add it later in the Plans section',
    addonYear: '{y} € a year — two months free',
    forwarderTab: 'I am a carrier or forwarder',
    importerTab: 'I am a cargo owner',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlyDiscount: '−17%',
    perMonth: '/mo',
    perYear: '/yr',
    yearlyEquivalent: '/mo billed annually',
    popular: 'Popular',
    yearlyHint: 'Save 2 months of payment — the annual subscription comes with a 17% discount',
    freeWord: 'Free',
    ctaFree: 'Start for free',
    priceFrom: 'from ',
    pricePer: '€ per',
    units: { check: 'check', other: 'item' },
    limits: { seats: 'Team members', tenders: 'Tenders per month', deals: 'Deals per month', partners: 'Partners', landings: 'Landing pages', bots: 'Telegram bots', storage: 'Storage', unlimited: 'unlimited', mb: 'MB', gb: 'GB' },
  },
  uk: {
    badge: 'Тарифи',
    title: 'Прозорі ціни',
    subtitle: 'Виберіть відповідний тариф для вашого типу бізнесу. Можна змінювати будь-якої миті.',
    freeNote: 'Кожен новий акаунт отримує 14 днів безкоштовного тріалу. Доповнення підключаються окремо — під час реєстрації або пізніше',
    addonBadge: 'Доповнення',
    addonCta: 'Обрати при реєстрації',
    addonLater: 'Не обрали одразу — додасте пізніше в розділі «Тарифи»',
    addonYear: '{y} € на рік — два місяці в подарунок',
    forwarderTab: 'Я перевізник або експедитор',
    importerTab: 'Я вантажовласник',
    monthly: 'Щомісяця',
    yearly: 'Річна',
    yearlyDiscount: '−17%',
    perMonth: '/міс',
    perYear: '/рік',
    yearlyEquivalent: '/міс при оплаті за рік',
    popular: 'Популярний',
    yearlyHint: 'Економія 2 місяці оплати — річна підписка йде зі знижкою 17%',
    freeWord: 'Безкоштовно',
    ctaFree: 'Почати безкоштовно',
    priceFrom: 'від ',
    pricePer: '€ за',
    units: { check: 'перевірку', other: 'штуку' },
    limits: { seats: 'Співробітники', tenders: 'Тендерів на місяць', deals: 'Угод на місяць', partners: 'Партнерів', landings: 'Лендингів', bots: 'Telegram-ботів', storage: 'Сховище', unlimited: 'без обмежень', mb: 'МБ', gb: 'ГБ' },
  },
}

export function LandingPricingTabs({
  locale,
  plans,
  ctaText,
  ctaHref,
}: LandingPricingTabsProps) {
  const loc = (['ru', 'ro', 'en', 'uk'].includes(locale) ? locale : 'ru') as Locale
  const t = I18N[loc]

  const [role, setRole] = useState<Role>('forwarder')
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly')

  // Локальная валюта по стране из IP (/api/geo/currency, кука на 24ч):
  // под ценой в EUR показываем «≈ N MDL/RUB/UAH/…». Язык страницы тут не
  // показатель — русскоязычный юзер в Молдове должен видеть леи.
  const [geoPricing, setGeoPricing] = useState<{ code: string; symbol: string; rate: number } | null>(null)
  useEffect(() => {
    let abort = false
    fetch('/api/geo/currency', { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (!abort && j?.pricing) setGeoPricing(j.pricing) })
      .catch(() => {})
    return () => { abort = true }
  }, [])

  // Фильтруем тарифы — только активные платные планы для выбранной роли
  // Trial и Free не показываем в карточках — для них отдельная заметка сверху.
  const visiblePlans = plans
    .filter(p => p.is_active && p.id !== 'trial' && p.id !== 'free')
    .filter(p => {
      const aud = (p as any).audience ?? 'forwarder'
      return aud === role
    })
    .sort((a, b) => a.sort_order - b.sort_order)

  // «Популярный» тариф приходит ИЗ БАЗЫ (subscription_plans.is_popular).
  //
  // Здесь был захардкоженный список id: forwarder → 'pro'. После разделения
  // линий экспедиторский тариф называется 'fw_pro' — совпадение пропало,
  // и подсветка исчезла молча, никто не заметил.
  //
  // Какой тариф рекомендовать — решение маркетинговое, а не программистское.
  // Ему место в таблице, где его меняют без релиза.

  return (
    <div className="mt-8">
      {/* 7xl, а не 6xl: в ряду из четырёх тарифов на 6xl карточка выходила
          239px, кнопке доставалось 177px — и «Получить ранний доступ» ложился
          в две строки. Здесь rem считается от 14px (html в globals.css), так
          что 7xl — это 1120px, а не привычные 1280: ряд по-прежнему помещается
          с запасом от breakpoint xl. */}
      <div className="max-w-7xl mx-auto">
        {/* Заметка про триал */}
        <div className="mb-8 mx-auto max-w-2xl rounded-lg px-4 py-3 flex items-center justify-center gap-2 text-sm" style={{ background: 'color-mix(in srgb, var(--accent) 7%, white)', border: '1px solid color-mix(in srgb, var(--accent) 18%, white)', color: 'color-mix(in srgb, var(--accent) 75%, black)' }}>
          <Gift size={16} className="shrink-0" />
          <span>{t.freeNote}</span>
        </div>

        {/* Переключатель ролей */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-0.5 rounded-full p-1 border-hairline border-ink-200 bg-ink-100">
            <button
              type="button"
              onClick={() => setRole('forwarder')}
              className={
                'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ' +
                (role === 'forwarder'
                  ? 'bg-white text-ink-900 shadow-sm'
                  : 'text-ink-500 hover:text-ink-900')
              }
            >
              <Truck size={14} />
              {t.forwarderTab}
            </button>
            <button
              type="button"
              onClick={() => setRole('importer')}
              className={
                'inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ' +
                (role === 'importer'
                  ? 'bg-white text-ink-900 shadow-sm'
                  : 'text-ink-500 hover:text-ink-900')
              }
            >
              <Gavel size={14} />
              {t.importerTab}
            </button>
          </div>
        </div>

        {/* Переключатель Monthly / Yearly с акцентом на скидку */}
        <div className="flex flex-col items-center gap-3 mb-10">
          <div className="inline-flex items-center rounded-lg bg-ink-100 p-1">
            <button
              type="button"
              onClick={() => setPeriod('monthly')}
              className={
                'px-4 py-1.5 rounded-md text-xs font-medium transition ' +
                (period === 'monthly' ? 'bg-white shadow-sm text-ink-900' : 'text-ink-600')
              }
            >
              {t.monthly}
            </button>
            <button
              type="button"
              onClick={() => setPeriod('yearly')}
              className={
                'px-4 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5 ' +
                (period === 'yearly' ? 'bg-white shadow-sm text-ink-900' : 'text-ink-600')
              }
            >
              {t.yearly}
              <span className={
                'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold ' +
                (period === 'yearly' ? 'text-white' : 'bg-ink-200 text-ink-600')
              }
              style={period === 'yearly' ? { background: 'var(--accent)' } : undefined}>
                {t.yearlyDiscount}
              </span>
            </button>
          </div>

          {/* Подсветка выгоды годовой подписки */}
          {period === 'yearly' && (
            <div className="text-[11px] inline-flex items-center gap-1.5 animate-fade-up text-accent-700">
              <Check size={11} strokeWidth={2.5} />
              {t.yearlyHint}
            </div>
          )}
        </div>

        {/* Карточки тарифов */}
        <div className={
          'grid gap-5 items-start ' +
          (visiblePlans.length <= 3
            ? 'grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto'
            : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4')
        }>
          {visiblePlans.map(plan => (
            <PricingCard
              key={plan.id}
              plan={plan}
              period={period}
              locale={loc}
              t={t}
              isPopular={Boolean((plan as any).is_popular)}
              geoPricing={geoPricing}
              accent="primary"
              ctaText={ctaText}
              ctaHref={`${ctaHref}${ctaHref.includes('?') ? '&' : '?'}role=${role}`}
            />
          ))}
        </div>


        {/* ── Дополнение ───────────────────────────────────────────────────
            Полоса ПОСЛЕ тарифов, а не карточка в их ряду: в общем ряду цена
            дополнения читается как ещё один — дешёвый — тариф, и человек
            начинает сравнивать вместо того, чтобы добавлять.

            Название, описание и цена берутся ИЗ РЕЕСТРА модулей. Второго
            ЦЕНА берётся из реестра — она не имеет права расходиться: назовём
            на лендинге одно, а в кабинете счёт придёт на другое.

            А текст остаётся здесь, рядом с остальным лендингом: на витрине
            продают, в кабинете описывают, и формулировки законно разные. Плюс
            все компоненты этой папки держат тексты локально — тянуть сюда
            переводы приложения значило бы сломать порядок ради одной строки. */}
        {soldModules().map(m => m.price && (
          <div key={m.key}
            // Фон — классами: тёмная тема зальёт bg-white и снимет градиент; встроенный градиент оставлял
            // блок почти белым под светлым текстом тёмной темы (HzTpOe26, ревью).
            className="mt-8 rounded-2xl border-hairline p-5 md:p-6 flex flex-wrap gap-6 items-center bg-white bg-gradient-to-b from-[color:color-mix(in_srgb,var(--accent)_6%,white)] to-white"
            style={{ borderColor: 'color-mix(in srgb, var(--accent) 22%, white)' }}>
            <div className="flex-1 min-w-[280px]">
              <span className="inline-block rounded-full px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-wider"
                style={{ color: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) 12%, white)' }}>
                {t.addonBadge}
              </span>
              {/* ── ИЗ РЕЕСТРА, А НЕ ОДНОЙ СТРОКОЙ НА ВСЕХ ──────────────
                  Раньше здесь стояли `t.addonTitle` и `t.addonText` — одни на
                  все дополнения. Пока продавалось одно, это работало; со
                  вторым оба назвались «Менеджмент» с чужим описанием.
                  Заметили это глазами, на живой странице.

                  Название и описание берём из переводов — с 1 сентября это их
                  единственный источник: раньше те же тексты лежали ещё и полями
                  реестра, и две копии могли разойтись. Третье дополнение
                  появится с верной подписью само, как только будет переведено;
                  пока не переведено — ворота не пустят. */}
              <div className="mt-2 text-[17px] font-extrabold text-ink-900">
                {moduleTitle(m.key, loc)}
              </div>
              <p className="mt-1 text-[13.5px] leading-relaxed text-ink-600 max-w-[64ch]">
                {moduleSummary(m.key, loc)}
              </p>
            </div>

            <div className="text-right min-w-[190px]">
              {/* ── ДВА ВИДА ЦЕНЫ ────────────────────────────────────────
                  Подписка идёт сама и считается за месяц; расходуемая единица
                  кончается и покупается снова. Написать у проверок «1,2 €/мес»
                  значит соврать: человек не платит ежемесячно, он платит за
                  штуку. */}
              {m.price.kind === 'unit' ? (
                <>
                  {/* «от» — потому что 1,2 € это цена в самом большом пакете.
                      Без этого слова число выглядит как цена любой проверки, и
                      человек, купивший разовую за 3 €, сочтёт себя обманутым. */}
                  <div className="text-[26px] font-extrabold tabular-nums text-ink-900">
                    <span className="text-[15px] font-semibold text-ink-500">{t.priceFrom}</span>
                    {String(m.price.monthly).replace('.', ',')}{' '}
                    <span className="text-[13px] font-semibold text-ink-500">
                      {t.pricePer} {m.price.unit ? t.units[m.price.unit] : t.units.other}
                    </span>
                  </div>
                  {m.price.singlePrice ? (
                    <div className="text-[12px] text-ink-500 mt-0.5">
                      Разовая — {String(m.price.singlePrice).replace('.', ',')} €.
                      Три первые бесплатно
                    </div>
                  ) : (
                    <div className="text-[12px] text-ink-500 mt-0.5">
                      Три первые — бесплатно после регистрации
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-[26px] font-extrabold tabular-nums text-ink-900">
                    {m.price.monthly} <span className="text-[13px] font-semibold text-ink-500">€/мес</span>
                  </div>
                  <div className="text-[12px] text-ink-500 mt-0.5">
                    {t.addonYear.replace('{y}', String(m.price.yearly))}
                  </div>
                </>
              )}
              <a href={ctaHref}
                className="inline-block mt-3 rounded-[10px] px-5 py-2.5 text-[13px] font-bold text-white"
                style={{ background: 'var(--accent)' }}>
                {t.addonCta}
              </a>
              <div className="text-[11.5px] text-ink-500 mt-2 leading-snug max-w-[210px] ml-auto">
                {t.addonLater}
              </div>
            </div>
          </div>
        ))}

        <div className="text-center mt-7 text-[13.5px] text-ink-500">
          {t.freeNote}
        </div>
      </div>
    </div>
  )
}

// «Красивое» округление ориентировочной цены: 973 → 970, 13 812 → 13 800.
// Точность не нужна — это «≈», а ровные числа читаются как ориентир, а не счёт.
function roundNiceLocal(v: number): number {
  if (v < 100) return Math.round(v)
  if (v < 1000) return Math.round(v / 10) * 10
  return Math.round(v / 100) * 100
}

function PricingCard({
  plan,
  period,
  locale,
  t,
  isPopular,
  geoPricing,
  accent,
  ctaText,
  ctaHref,
}: {
  plan: SubscriptionPlan
  period: 'monthly' | 'yearly'
  locale: Locale
  t: typeof I18N['ru']
  isPopular: boolean
  /** Локальная валюта по IP (страна → код/символ/курс EUR→валюта) или null */
  geoPricing: { code: string; symbol: string; rate: number } | null
  accent?: 'primary'
  ctaText: string
  ctaHref: string
}) {
  const price = period === 'yearly'
    ? (plan.price_yearly ?? plan.price_monthly * 12)
    : plan.price_monthly
  const monthlyEquivalent = period === 'yearly' && plan.price_yearly
    ? plan.price_yearly / 12
    : null
  const free = price === 0
  const pop = isPopular
  // Локализованный контент из БД (subscription_plans.{description_i18n,features_i18n}).
  // Fallback на RU-поля description/features, если перевод для локали не задан.
  const description = plan.description_i18n?.[locale] ?? plan.description
  const features = plan.features_i18n?.[locale] ?? plan.features ?? []

  const baseShadow = pop
    ? '0 24px 60px -24px color-mix(in srgb, var(--accent) 53%, transparent)'
    : '0 1px 3px rgba(15,23,42,0.05)'
  const hoverShadow = pop
    ? '0 30px 70px -22px color-mix(in srgb, var(--accent) 62%, transparent)'
    : '0 16px 38px -16px rgba(15,23,42,0.22)'
  const baseLift = pop ? -10 : 0

  return (
    <div
      className="relative rounded-[20px] p-[30px] flex flex-col overflow-hidden"
      style={{
        background: pop ? SLATE[900] : '#ffffff',
        border: pop ? '1px solid transparent' : '1px solid #e2e8f0',
        boxShadow: baseShadow,
        transform: `translateY(${baseLift}px)`,
        transition: 'transform .16s ease, box-shadow .16s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = `translateY(${baseLift - 4}px)`
        e.currentTarget.style.boxShadow = hoverShadow
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = `translateY(${baseLift}px)`
        e.currentTarget.style.boxShadow = baseShadow
      }}
    >
      {pop && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(80% 50% at 90% 0%, color-mix(in srgb, var(--accent) 16%, transparent), transparent 60%)' }}
        />
      )}
      <div className="relative flex flex-col flex-1">
        {/* Заголовок + бейдж */}
        <div className="flex items-center gap-2.5">
          <h3 className="text-[21px] font-extrabold tracking-[-0.01em]" style={{ color: pop ? '#fff' : SLATE[900] }}>{plan.name}</h3>
          {pop && (
            <span className="text-[11px] font-bold text-white px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: 'var(--accent)' }}>
              {t.popular}
            </span>
          )}
        </div>
        {description && (
          <p className="text-[13.5px] leading-[1.5] mt-2.5 mb-5 min-h-[40px]" style={{ color: pop ? SLATE[400] : SLATE[500] }}>
            {description}
          </p>
        )}

        {/* Цена */}
        {/* Фиксированная высота строки цены + leading-none: у «Бесплатно» (30px)
            и цифр (42px) разные строки, из-за наследуемого line-height
            разделители карточек расходились по вертикали */}
        <div className="flex items-end gap-1.5 h-[42px]">
          {free ? (
            /* «Бесплатно» — девять букв, а не три символа как «€49». Тем же
               кеглем 42px слово не помещалось в карточку и вылезало за край. */
            <span className="text-[30px] leading-none font-extrabold tracking-[-0.02em] whitespace-nowrap" style={{ color: pop ? '#fff' : SLATE[900] }}>{t.freeWord}</span>
          ) : (
            <>
              <span className="text-[42px] leading-none font-extrabold tracking-[-0.03em]" style={{ color: pop ? '#fff' : SLATE[900], fontFamily: MONO }}>€{price}</span>
              <span className="text-sm" style={{ color: pop ? SLATE[400] : SLATE[500] }}>{period === 'yearly' ? t.perYear : t.perMonth}</span>
            </>
          )}
        </div>
        {/* Ориентир в местной валюте (страна — по IP). Оплата остаётся в EUR.
            На бесплатном тарифе — невидимая строка-заглушка, чтобы разделитель
            и фичи всех карточек стояли на одной линии. */}
        {geoPricing && (
          <p className={'text-[11px] mt-1' + (free ? ' invisible' : '')} style={{ color: pop ? SLATE[500] : SLATE[400], fontFamily: MONO }}>
            {free ? '≈' : (
              <>
                ≈ {roundNiceLocal(price * geoPricing.rate).toLocaleString(locale)} {geoPricing.code}
                {period === 'yearly' ? t.perYear : t.perMonth}
              </>
            )}
          </p>
        )}
        {monthlyEquivalent && (
          <p className="text-[11px] mt-1" style={{ color: pop ? SLATE[500] : SLATE[400], fontFamily: MONO }}>
            ≈ €{monthlyEquivalent.toFixed(0)} {t.yearlyEquivalent}
          </p>
        )}

        <div className="my-[22px] h-px" style={{ background: pop ? SLATE[700] : SLATE[200] }} />

        {/* ── ЛИМИТЫ ТАРИФА ──────────────────────────────────────────────
            Сколько людей, тендеров, сделок и записей даёт тариф — из самого
            тарифа (subscription_plans.max_*), те же числа, что в кабинете.
            Прежде на витрине были только слова. */}
        <div className="flex flex-col gap-1.5 mb-5">
          {planLimits(plan).map(l => (
            <div key={l.key} className="flex items-baseline justify-between gap-3 text-[13px]">
              <span style={{ color: pop ? SLATE[400] : SLATE[500] }}>{t.limits[l.key]}</span>
              <span className="font-semibold tabular-nums text-right" style={{ color: pop ? '#fff' : SLATE[900] }}>
                {l.value == null
                  ? t.limits.unlimited
                  : l.key === 'storage'
                    ? (() => { const st = storageSize(l.value); return `${st.value.toLocaleString(locale)} ${t.limits[st.unit]}` })()
                    : l.value.toLocaleString(locale)}
              </span>
            </div>
          ))}
        </div>

        {/* Фичи */}
        <div className="flex flex-col gap-[11px] mb-[26px] flex-1">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="shrink-0 mt-0.5" style={{ color: 'var(--accent)' }}><Check size={16} strokeWidth={2.4} /></span>
              <span className="text-sm" style={{ color: pop ? SLATE[300] : SLATE[700] }}>{f}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          href={ctaHref}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-[11px] text-sm font-bold px-3 py-3 transition-all hover:-translate-y-0.5"
          style={pop
            ? { background: '#ffffff', color: SLATE[900] }
            : { background: '#ffffff', color: SLATE[900], border: '1px solid #e2e8f0' }}
        >
          {free ? t.ctaFree : ctaText}
          <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  )
}
